import { Node as PmNode } from 'prosemirror-model'
import { Decoration, DecorationSet } from 'prosemirror-view'

import {
  DEFAULT_CONTD_DISPLAY_TEXT,
  DEFAULT_MORE_DISPLAY_TEXT,
  getPrintDate,
  getPrintTimestamp,
} from '#util'

import type { RevisionSet } from '../revisions'

import {
  inlinePageBreakWidget,
  PageData,
  PageDecorationCallbacks,
  PageDecorationContext,
  pageFooterWidget,
  pageHeaderWidget,
  PageLayout,
  revisionRailWidget,
  ScriptData,
} from './widgets'

type SharedContext = Omit<PageDecorationContext, 'page'>
type DecorationFactory = ReturnType<typeof createPageDecorationFactory>

// BoundaryKind determines which widgets are rendered at a page boundary
export enum BoundaryKind {
  // normal page splitting at block boundary or manual page break,
  // no special handling needed
  Normal = 'normal',
  // split a prose block with aligned page widgets and no dialogue markers
  InlineSplit = 'inlineSplit',
  // show more/contd without splitting dialogue node
  // (e.g. when page break falls within a dialogue block and we use a inline decoration)
  DialogueContinuation = 'dialogueContinuation',
  // show more/contd when dialogue continues across a page break, different dialogues are adjacent to the page break
  // (e.g. when page break falls between dialogue blocks)
  AdjacentDialogues = 'adjacentDialogues',
}

// Page-shaped decoration input built from pagination output and consumed by the
// page-decoration renderer.
export interface PageDecorationData {
  // Document span for the rendered page and remaining blank footer space.
  start: number
  end: number
  shortfall: number

  // Boundary behavior determines which widgets are rendered at the page break.
  boundaryKind: BoundaryKind

  // Page numbering and break state used by headers, footers, and lock markers.
  pageNumber: number
  pageAlias?: string
  isLockedPageBreak: boolean
  isManualBreak?: boolean

  // Optional metadata rendered by page-scoped decorations.
  pageSection?: string
  sceneContdHeader?: boolean
  sceneContdFooter?: boolean
  sceneNumber?: string
  sceneElementNumber?: string
  latestRevision?: RevisionSet
  isTitlePage?: boolean
}

export interface PageDecorationBuildOptions {
  callbacks?: PageDecorationCallbacks
  pageMeta?: PageLayout
  moresContds?: PageDecorationContext['moresContds']
  totalPages?: number
  scriptMeta?: Partial<Omit<ScriptData, 'pages'>>
}

// Represents the data needed to plan the decorations for a page boundary (i.e. the transition between two pages).
interface PageBoundary {
  current: PageDecorationData
  next: PageDecorationData
  boundaryPos: number
  speaker: string | undefined
}

// -----------------------------------------------------------------------------
// Public API
// -----------------------------------------------------------------------------
export function buildPaginationDecorations(
  doc: PmNode,
  pages: PageDecorationData[],
  options: PageDecorationBuildOptions,
): DecorationSet {
  if (pages.length === 0) return DecorationSet.empty
  const resolvedPages = resolvePageNumbers(pages)

  const factory = createPageDecorationFactory({
    script: createScriptData(
      options.totalPages ?? resolvedPages.length,
      options,
    ),
    layout: options.pageMeta,
    moresContds: options.moresContds,
    callbacks: options.callbacks,
  })

  return DecorationSet.create(
    doc,
    buildPageBoundaryDecorations(resolvedPages, options, factory),
  )
}

export function buildRevisionRailDecorations(
  pages: PageDecorationData[],
): Decoration[] {
  const railDecorations: Decoration[] = []
  for (const page of pages) {
    if (page.latestRevision) {
      railDecorations.push(revisionRailWidget(page.start, page.latestRevision))
    }
  }
  return railDecorations
}

function createScriptData(
  totalPages: number,
  options: PageDecorationBuildOptions,
): ScriptData {
  return {
    pages: totalPages,
    title: options.scriptMeta?.title,
    date: options.scriptMeta?.date ?? getPrintDate(),
    timestamp: options.scriptMeta?.timestamp ?? getPrintTimestamp(),
    duration: options.scriptMeta?.duration,
    activeRevision: options.scriptMeta?.activeRevision,
  }
}

function buildPageBoundaryDecorations(
  pages: PageDecorationData[],
  options: PageDecorationBuildOptions,
  factory: DecorationFactory,
): Decoration[] {
  const decorations: Decoration[] = [factory.headerContainer(pages[0])]

  for (let pageIndex = 0; pageIndex < pages.length - 1; pageIndex++) {
    const boundary = readBoundary(pages, pageIndex, options)
    decorations.push(...planBoundary(boundary, factory))
  }

  decorations.push(factory.footerContainer(pages.at(-1)!))

  return decorations
}

function readBoundary(
  pages: PageDecorationData[],
  pageIndex: number,
  options: PageDecorationBuildOptions,
): PageBoundary {
  const current = pages[pageIndex]
  const next = pages[pageIndex + 1]

  return {
    current,
    next,
    boundaryPos: current.end,
    speaker:
      current.end > current.start
        ? options.callbacks?.getSpeaker?.(current.end - 1)
        : undefined,
  }
}

function planBoundary(
  boundary: PageBoundary,
  factory: DecorationFactory,
): Decoration[] {
  switch (boundary.current.boundaryKind) {
    case BoundaryKind.Normal:
      return [
        factory.footerContainer(boundary.current),
        factory.headerContainer(boundary.next),
      ]

    case BoundaryKind.InlineSplit:
      return [factory.inlineSplit(boundary.current, boundary.next)]

    case BoundaryKind.DialogueContinuation: {
      const key = buildContinuationKey(boundary)
      return [
        factory.continuation(
          boundary.current,
          boundary.next,
          boundary.speaker,
          key,
        ),
      ]
    }

    case BoundaryKind.AdjacentDialogues: {
      const key = buildContinuationKey(boundary)
      return [
        factory.footerContainer(boundary.current, key),
        factory.headerContainer(boundary.next, {
          speaker: boundary.speaker,
          key,
        }),
      ]
    }
  }
}

// Factory for function that create page decoration widgets with shared context (e.g. script and layout data).
function createPageDecorationFactory(shared: SharedContext) {
  const pageCtx = (page: PageDecorationData): PageDecorationContext => {
    const pageData: PageData = {
      pageNumber: page.pageNumber,
      pageAlias: page.pageAlias,
      pageSection: page.pageSection,
      sceneContdHeader: page.sceneContdHeader,
      sceneContdFooter: page.sceneContdFooter,
      sceneNumber: page.sceneNumber,
      sceneElementNumber: page.sceneElementNumber,
      latestRevision: page.latestRevision,
      isTitlePage: page.isTitlePage,
    }
    return {
      ...shared,
      page: pageData,
      layout: pageData.isTitlePage ? undefined : shared.layout,
    }
  }

  return {
    headerContainer(
      page: PageDecorationData,
      contd?: { speaker: string | undefined; key: string },
    ): Decoration {
      return pageHeaderWidget(page.start, pageCtx(page), {
        isManualBreak: contd ? false : (page.isManualBreak ?? false),
        isLockedPageBreak: page.isLockedPageBreak,
        continuationText: contd ? buildContdText(contd.speaker) : undefined,
        contdKey: contd?.key,
      })
    },

    footerContainer(page: PageDecorationData, moreKey?: string): Decoration {
      return pageFooterWidget(
        page.end,
        pageCtx(page),
        page.shortfall,
        moreKey ? { text: DEFAULT_MORE_DISPLAY_TEXT, key: moreKey } : undefined,
      )
    },

    continuation(
      current: PageDecorationData,
      next: PageDecorationData,
      speaker: string | undefined,
      key: string,
    ): Decoration {
      return inlinePageBreakWidget(
        current.end,
        pageCtx(current),
        current.shortfall,
        pageCtx(next),
        DEFAULT_MORE_DISPLAY_TEXT,
        buildContdText(speaker),
        key,
      )
    },

    inlineSplit(
      current: PageDecorationData,
      next: PageDecorationData,
    ): Decoration {
      const key = [
        'inline-split',
        current.pageNumber,
        next.pageNumber,
        current.end,
        current.latestRevision?.label ?? '',
        next.latestRevision?.label ?? '',
      ].join(':')
      return inlinePageBreakWidget(
        current.end,
        pageCtx(current),
        current.shortfall,
        pageCtx(next),
        undefined,
        undefined,
        key,
      )
    },
  }
}

export function buildContdText(speaker: string | undefined): string {
  return speaker
    ? `${speaker} ${DEFAULT_CONTD_DISPLAY_TEXT}`
    : DEFAULT_CONTD_DISPLAY_TEXT
}

function buildContinuationKey(boundary: PageBoundary): string {
  return [
    'dialogue-continuation',
    boundary.current.pageNumber,
    boundary.next.pageNumber,
    boundary.boundaryPos,
    boundary.speaker?.split(' ').join('-') ?? '',
    boundary.current.latestRevision?.label ?? '',
    boundary.next.latestRevision?.label ?? '',
  ].join(':')
}

function resolvePageNumbers(pages: PageDecorationData[]): PageDecorationData[] {
  return pages.map((page, index) => ({
    ...page,
    pageNumber: page.pageNumber ?? index + 1,
  }))
}
